import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../index.js';

// Define the state type
interface UiState {
  // General UI state
  isLoading: boolean;
  
  // Notifications
  notifications: Notification[];
  
  // Modal state
  activeModal: string | null;
  modalData: any;
}

// Notification type
interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
  autoClose?: boolean;
  duration?: number;
}

// Initial state
const initialState: UiState = {
  isLoading: false,
  notifications: [],
  activeModal: null,
  modalData: null,
};

// Create the slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    // Notifications
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const id = Date.now().toString();
      state.notifications.push({
        id,
        ...action.payload,
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    
    // Modal state
    openModal: (state, action: PayloadAction<{ modalId: string, data?: any }>) => {
      state.activeModal = action.payload.modalId;
      state.modalData = action.payload.data || null;
    },
    closeModal: (state) => {
      state.activeModal = null;
      state.modalData = null;
    },
  },
});

// Export actions
export const {
  setLoading,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
} = uiSlice.actions;

// Export selectors
export const selectIsLoading = (state: RootState) => state.ui.isLoading;
export const selectNotifications = (state: RootState) => state.ui.notifications;
export const selectActiveModal = (state: RootState) => state.ui.activeModal;
export const selectModalData = (state: RootState) => state.ui.modalData;

// Export reducer
export default uiSlice.reducer;